import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const INITIAL_USERS = [
  {
    id: 'usr-1',
    name: 'Admin User',
    email: 'admin@example.com',
    phone: '+1 (555) 019-2834',
    password: 'Password123!',
    role: 'Admin',
    createdAt: new Date(Date.now() - 86400000 * 5).toISOString(),
    status: 'Active'
  },
  {
    id: 'usr-2',
    name: 'Jane Doe',
    email: 'jane.doe@example.com',
    phone: '+1 (555) 432-8765',
    password: 'Password123!',
    role: 'Registered User',
    createdAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    status: 'Active'
  },
  {
    id: 'usr-3',
    name: 'John Smith',
    email: 'john.smith@example.com',
    phone: '+1 (555) 789-0123',
    password: 'Password123!',
    role: 'Registered User',
    createdAt: new Date(Date.now() - 86400000 * 1).toISOString(),
    status: 'Active'
  }
];

export const AuthProvider = ({ children }) => {
  const [users, setUsers] = useState(() => {
    try {
      const saved = localStorage.getItem('proto_users');
      return saved ? JSON.parse(saved) : INITIAL_USERS;
    } catch (e) {
      return INITIAL_USERS;
    }
  });

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const saved = localStorage.getItem('proto_current_user');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('proto_users', JSON.stringify(users));
    } catch (e) {
      console.error('Failed to save users to localStorage', e);
    }
  }, [users]);

  useEffect(() => {
    try {
      if (currentUser) {
        localStorage.setItem('proto_current_user', JSON.stringify(currentUser));
      } else {
        localStorage.removeItem('proto_current_user');
      }
    } catch (e) {
      console.error('Failed to save current user to localStorage', e);
    }
  }, [currentUser]);

  // Register a new user
  const registerUser = ({ name, email, phone, password, role = 'Registered User' }) => {
    // Basic validation
    if (!name || !email || !phone || !password) {
      return { success: false, message: 'All fields (Name, Email, Phone Number, Password) are required.' };
    }

    // Check if email already exists
    const existing = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    if (existing) {
      return { success: false, message: 'An account with this email address already exists.' };
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { success: false, message: 'Please enter a valid email address.' };
    }

    // Phone validation (simple check for numbers/chars)
    if (phone.replace(/\D/g, '').length < 7) {
      return { success: false, message: 'Please enter a valid phone number.' };
    }

    // Password strength check
    if (password.length < 6) {
      return { success: false, message: 'Password must be at least 6 characters long.' };
    }

    const newUser = {
      id: 'usr-' + Date.now(),
      name: name.trim(),
      email: email.trim().toLowerCase(),
      phone: phone.trim(),
      password,
      role,
      createdAt: new Date().toISOString(),
      status: 'Active'
    };

    setUsers(prev => [newUser, ...prev]);
    return { success: true, user: newUser, message: 'Registration successful! You can now log in.' };
  };

  // Login user
  const loginUser = (email, password) => {
    if (!email || !password) {
      return { success: false, message: 'Email and password are required.' };
    }

    const found = users.find(
      u => u.email.toLowerCase() === email.toLowerCase() && u.password === password
    );

    if (!found) {
      return { success: false, message: 'Invalid email or password.' };
    }

    if (found.status === 'Suspended' || found.status === 'Inactive') {
      return { success: false, message: 'Your account has been suspended or deactivated.' };
    }

    setCurrentUser(found);
    return { success: true, user: found, message: `Welcome back, ${found.name}!` };
  };

  // Logout user
  const logoutUser = () => {
    setCurrentUser(null);
    return { success: true, message: 'Logged out successfully.' };
  };

  // Update user (for profile or admin management)
  const updateUser = (userId, updatedData) => {
    let updatedUserResult = null;
    setUsers(prev =>
      prev.map(u => {
        if (u.id === userId) {
          const updated = { ...u, ...updatedData };
          if (currentUser && currentUser.id === userId) {
            updatedUserResult = updated;
          }
          return updated;
        }
        return u;
      })
    );

    if (updatedUserResult) {
      setCurrentUser(updatedUserResult);
    }
    return { success: true, message: 'User updated successfully.' };
  };

  // Delete user (Registration Management)
  const deleteUser = (userId) => {
    setUsers(prev => prev.filter(u => u.id !== userId));
    if (currentUser && currentUser.id === userId) {
      setCurrentUser(null);
    }
    return { success: true, message: 'User registration removed successfully.' };
  };

  // Reset demo data
  const resetDemoData = () => {
    setUsers(INITIAL_USERS);
    setCurrentUser(null);
    localStorage.removeItem('proto_users');
    localStorage.removeItem('proto_current_user');
    return { success: true, message: 'Reset to default prototype data.' };
  };

  return (
    <AuthContext.Provider
      value={{
        users,
        currentUser,
        registerUser,
        loginUser,
        logoutUser,
        updateUser,
        deleteUser,
        resetDemoData
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
